'use client';

import { useEffect, useState, useRef } from 'react';
import { useParams, useRouter } from 'next/navigation';
import Link from 'next/link';
import VehicleDetailSection from '@/components/vehicles/VehicleDetailSection';
import { Icon } from '@iconify/react';
import useClientStore from '@/store/useClientStore';
import useCustomerStore from '@/store/useCustomerStore';
import { Client, Vehicle } from '@/utils/types';
import { CustomerDataModal } from '@/components/customers/CustomerDataModal';
import { useTranslation } from '@/i18n/hooks/useTranslation';
import { getVehicleById, incrementVehicleViews } from '@/lib/vehicles';
import { cameFromList } from '@/lib/listNavigation';
import type { PrecioWebModo } from '@/lib/precio-web';

interface VehicleDetailsPageClientProps {
  /** Vehículo ya fetcheado en el server (page.tsx) — evita el skeleton y el doble fetch */
  initialVehicle?: Vehicle | null;
  /** Cliente del header x-client-data — fallback hasta que el store hidrate (trae currency) */
  initialClient?: Client | null;
  /** URL canónica de la ficha, armada server-side (evita window.location en el primer render) */
  pageUrl?: string;
  /** Ticket #171: modo de precio de la automotora, leído en el server de la fila del tenant */
  precioModo?: PrecioWebModo;
}

export default function VehicleDetailsPageClient({
  initialVehicle = null,
  initialClient = null,
  pageUrl,
  precioModo,
}: VehicleDetailsPageClientProps) {
  const { t } = useTranslation();
  const params = useParams();
  const router = useRouter();
  const { client: storeClient } = useClientStore();
  const [vehicle, setVehicle] = useState<Vehicle | null>(initialVehicle);
  const [loading, setLoading] = useState(!initialVehicle);
  const isIncrementingRef = useRef(false);
  // El store (fila completa + config) gana cuando carga; initialClient cubre el SSR
  const client = (storeClient ?? initialClient) as Client;

  // ¿Esta ficha se abrió desde un listado? La respuesta es por ruta y siempre
  // la misma dentro de esta visita, así que se puede leer en el render. Si el
  // usuario saltó acá desde otra ficha (vehículos similares) o desde un link
  // de Google, no hay marca y el "Volver" empuja la URL del listado.
  const openedFromList = params.id ? cameFromList(`/vehicles/${params.id}`) : false;
  const {
    customer,
    likes,
    toggleLike,
    isModalOpen,
    setIsModalOpen,
    initializeCustomer,
  } = useCustomerStore();

  const registerView = async () => {
    if (!params.id) return;
    // Solo incrementar si no está en proceso y no está en sessionStorage.
    // Queda client-side a propósito: bots sin JS no inflan las vistas.
    const viewKey = `vehicle_view_${params.id}`;
    if (!isIncrementingRef.current && !sessionStorage.getItem(viewKey)) {
      isIncrementingRef.current = true;
      try {
        await incrementVehicleViews(Number(params.id));
        sessionStorage.setItem(viewKey, 'true');
      } finally {
        isIncrementingRef.current = false;
      }
    }
  };

  const fetchVehicle = async () => {
    if (!params.id) return;
    try {
      const response = await getVehicleById(params.id as string, client?.id);
      setVehicle(response);
      setLoading(false);
      // La vista se registra solo si la ficha existe de verdad — un id
      // inexistente o de otro tenant no debe escribir el RPC ni inflar vistas.
      if (response) registerView();
    } catch (error) {
      console.error('Error fetching vehicle:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    // Con initialVehicle del server no hay nada que re-fetchear: solo la vista.
    if (initialVehicle) {
      registerView();
    } else {
      fetchVehicle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  const handleLike = async (vehicleId: string) => {
    if (!customer) {
      setIsModalOpen(true);
    } else {
      await toggleLike(vehicleId);
    }
  };

  return (
    <div className='container mx-auto px-4 py-20 bg-white dark:bg-dark-bg min-h-screen'>
      <div className='mb-8'>
        <Link
          href='/vehicles'
          prefetch={false}
          onClick={(e) => {
            // Si venimos del listado, volver es un Atrás de verdad: no apila
            // una entrada nueva (antes el botón Atrás del navegador devolvía a
            // la ficha, en bucle) y dispara el `popstate` con el que el listado
            // recupera su posición y sus "Ver más".
            if (openedFromList) {
              e.preventDefault();
              router.back();
              return;
            }
            // Entrada directa (Google, WhatsApp) o salto entre fichas: atrás no
            // hay listado. El listado guarda su última URL (con filtros/?promo)
            // en sessionStorage, así que volver ahí conserva la campaña. El
            // href queda como fallback crawleable.
            try {
              const last = sessionStorage.getItem('vehicles-list-url');
              if (last && last !== '/vehicles') {
                e.preventDefault();
                router.push(last);
              }
            } catch {}
          }}
          className='flex items-center gap-2 text-gray-600 hover:text-gray-800 dark:text-gray-400  dark:hover:text-white transition-colors'
        >
          <Icon icon='mdi:arrow-left' className='text-xl' />
          <span>{t('pages.vehicleDetails.backToVehicles')}</span>
        </Link>
      </div>
      <VehicleDetailSection
        vehicle={vehicle}
        loading={loading}
        client={client}
        pageUrl={pageUrl}
        locationKnown={!!storeClient}
        precioModo={precioModo}
        onLike={handleLike}
        isLiked={likes.includes(vehicle?.id || '')}
        showLikeButton={true}
      />

      <CustomerDataModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={async (customerData) => {
          await initializeCustomer({
            ...customerData,
            client_id: client?.id || '',
          });
          if (vehicle) {
            await toggleLike(vehicle.id);
          }
        }}
      />
    </div>
  );
}
